import React, { useId } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { FOCUS_RING_ONLY, FIELD_HELP_CLASS, FIELD_LABEL_ERROR_CLASS } from './FieldShell';

/**
 * The checkbox.
 *
 * Its id used to come from `Math.random()`, which produces a different value on
 * the server and the client and so breaks the `htmlFor`/`id` pairing on hydration
 * - clicking the label did nothing until React caught up. `useId()` is stable
 * across both renders.
 *
 * `disabled`, `error` and `description` are additive; every existing call site
 * passing only `{label, checked, onChange}` renders exactly as before apart from
 * the champagne accent.
 */

interface CheckboxProps {
  id?: string;
  label: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  disabled?: boolean;
  /** Error message - renders in danger tone under the label. */
  error?: string;
  /** Muted second line under the label. */
  description?: string;
  /** Skips `t()` for callers passing already-translated labels. */
  translate?: boolean;
  /**
   * Keeps the label in the accessibility tree but out of the layout - for
   * checkboxes in a table cell, where the column header already carries the
   * visible name and a second copy per row would be noise. The label is still
   * required: a checkbox with no accessible name is announced as unlabelled.
   */
  hideLabel?: boolean;
}

const Checkbox: React.FC<CheckboxProps> = ({
  id,
  label,
  checked,
  onChange,
  className,
  disabled = false,
  error,
  description,
  translate = true,
  hideLabel = false,
}) => {
  const { t } = useTranslation();
  const generatedId = useId();
  const uniqueId = id || generatedId;
  const descriptionId = description ? `${uniqueId}-description` : undefined;
  const errorId = error ? `${uniqueId}-error` : undefined;
  const tr = (value: string) => (translate ? t(value) : value);

  return (
    <div className={clsx(className)}>
      <div
        className={clsx(
          'flex items-start gap-2',
          hideLabel && 'gap-0',
          disabled && 'opacity-60'
        )}
      >
        <input
          id={uniqueId}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={clsx(descriptionId, errorId) || undefined}
          className={clsx(
            'mt-0.5 h-4 w-4 flex-shrink-0 rounded accent-champagne',
            error ? 'border-danger-500' : 'border-hairline',
            FOCUS_RING_ONLY,
            'transition duration-150 ease-in-out',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
        />
        <div className="min-w-0">
          <label
            htmlFor={uniqueId}
            className={clsx(
              hideLabel ? 'sr-only' : 'block text-sm leading-5',
              error ? FIELD_LABEL_ERROR_CLASS : 'text-fg-body',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            )}
          >
            {tr(label)}
          </label>
          {description && (
            <p id={descriptionId} className={clsx('mt-0.5', FIELD_HELP_CLASS)}>
              {tr(description)}
            </p>
          )}
        </div>
      </div>
      {error && (
        <p id={errorId} className="mt-1.5 text-xs text-danger-500" role="alert">
          {tr(error)}
        </p>
      )}
    </div>
  );
};

export default Checkbox;
